/**
 * Shared TypeBox schemas for Hub-owned plan cards (COP-057/COP-072).
 *
 * The key security boundary is the split between:
 *  - `PlanRowSchema` / `PlanPersistedStepSchema`: durable, RLS-protected server
 *    state that may contain executable args; and
 *  - `PlanPendingPartSchema` / `PlanPreviewStepSchema`: browser-visible SSE
 *    state that may contain only redacted `argsPreview` data.
 *
 * The stream part is emitted as an AI SDK `data-tf-plan-pending` chunk whose
 * `data` payload validates against `PlanPendingPartSchema` and carries
 * `type: 'tf-plan-pending'` on the wire.
 */
import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, JsonObjectSchema, UuidSchema } from './common.js';
/** Lifecycle status of a stored `tf_plan` row (COP-057). */
export const PlanStatusSchema = Type.Union([
    Type.Literal('pending'),
    Type.Literal('approved'),
    Type.Literal('rejected'),
    Type.Literal('edited'),
    Type.Literal('expired'),
]);
/** Closed union for plan step kind. `write` steps trigger the plan card gate. */
export const PlanStepKindSchema = Type.Union([Type.Literal('read'), Type.Literal('write')]);
/**
 * 64-character lowercase sha256 hex hash. Named separately from the approval
 * `PayloadHashSchema` to avoid accidental approval/plan import collisions while
 * preserving COP-011-style exact-payload binding for plans.
 */
export const PlanPayloadHashSchema = Type.String({
    minLength: 64,
    maxLength: 64,
    pattern: '^[a-f0-9]{64}$',
});
/** COP-071 append-only `tf_plan_decision.action` lifecycle values. */
export const PlanDecisionActionSchema = Type.Union([
    Type.Literal('approve'),
    Type.Literal('reject'),
    Type.Literal('edit_requested'),
    Type.Literal('expire'),
    Type.Literal('blanket_approve'),
    Type.Literal('execution_started'),
    Type.Literal('execution_completed'),
    Type.Literal('execution_failed'),
]);
/** Client-postable actions for `POST /plans/:id/decide`; lifecycle-only events are server-owned. */
export const PlanDecideActionSchema = Type.Union([
    Type.Literal('approve'),
    Type.Literal('reject'),
    Type.Literal('edit'),
]);
/**
 * COP-093 consent scope chosen by the user on the PlanCard / per-step consent
 * card. `per_turn` is the single-use `tf_approval` path (no standing grant);
 * `session` / `always` mint a standing `tf_consent_grant` (COP-093 §Consent
 * card four choices). The scope is set ONLY by the explicit control the user
 * clicks — never inferred from the model or track (COP-044).
 */
export const ConsentScopeSchema = Type.Union([
    Type.Literal('per_turn'),
    Type.Literal('session'),
    Type.Literal('always'),
]);
/** Actor namespace captured on immutable plan decision audit rows. */
export const PlanDecisionActorTypeSchema = Type.Union([
    Type.Literal('user'),
    Type.Literal('system'),
    Type.Literal('admin'),
]);
/** Path params shared by plan routes such as `POST /plans/:id/decide`. */
export const PlanIdParamsSchema = Type.Object({
    id: UuidSchema,
}, { additionalProperties: false });
/**
 * Decide request body for `POST /plans/:id/decide` (COP-071/COP-073).
 *
 * `payloadHash` is required to bind the decision to the exact stored plan.
 * `reason` carries rejection/edit text only; `wholePlanApproved` is an explicit
 * blanket-approval toggle for the current plan envelope. The exact object shape
 * intentionally rejects raw `steps`, `args`, and lifecycle-only actions.
 *
 * COP-093: `consentScope` records the user's PlanCard choice. The route mints a
 * session-scoped plan-envelope `tf_consent_grant` bound to `plan_id` +
 * `payload_hash` ONLY when `action='approve'` + `wholePlanApproved=true`
 * (Execute-plan); Execute-step-by-step (`approve`, no `wholePlanApproved`) and
 * Deny (`reject`) create no grant. Scope is user-only (COP-044).
 */
export const PlanDecideBodySchema = Type.Object({
    action: PlanDecideActionSchema,
    payloadHash: PlanPayloadHashSchema,
    reason: Type.Optional(Type.String({ maxLength: 2000 })),
    wholePlanApproved: Type.Optional(Type.Boolean()),
    consentScope: Type.Optional(ConsentScopeSchema),
}, { additionalProperties: false });
/** Optional UI reference attached to a preview step. Never execution authority. */
export const PlanStepReferenceSchema = Type.Object({
    type: Type.String({ minLength: 1, maxLength: 100 }),
    id: Type.String({ minLength: 1, maxLength: 500 }),
    label: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
    url: Type.Optional(Type.String({ minLength: 1, maxLength: 2048 })),
}, { additionalProperties: false });
/**
 * COP-119 — a declared reference from one step's argument to an EARLIER step's
 * output. This is the only form in which "act on the thing step 1 creates" can
 * be written down: the planner expresses it as the sentinel `$step.<i>.<path>`,
 * plan validation parses that into this explicit declaration, and execution
 * resolves EXCLUSIVELY from here (the sentinel is never honoured at execution).
 *
 * Resolution is backward-only (`fromStep` < the owning step's index) and
 * fail-closed: a source step that did not run, failed, or lacks `sourcePath`
 * halts the plan rather than dispatching a partial argument.
 */
export const PlanStepRefSchema = Type.Object({
    /** JSON path INTO the owning step's `args`, e.g. `id` or `items.0.id`. */
    path: Type.String({ minLength: 1, maxLength: 500 }),
    /** Source step index. MUST be < the owning step's index. */
    fromStep: Type.Integer({ minimum: 1 }),
    /** JSON path into the source step's recorded result content, e.g. `id`. */
    sourcePath: Type.String({ minLength: 1, maxLength: 500 }),
}, { additionalProperties: false });
/**
 * Persisted ordered step. This is the server-side execution authority and may
 * carry raw executable `args` inside the RLS-protected `tf_plan.steps_jsonb` row.
 */
export const PlanPersistedStepSchema = Type.Object({
    index: Type.Integer({ minimum: 1 }),
    toolName: Type.String({ minLength: 1, maxLength: 200 }),
    kind: PlanStepKindSchema,
    reason: Type.String({ minLength: 1, maxLength: 1000 }),
    /**
     * COP-122 — the planner-authored one-line description of what THIS step
     * does, grounded in the step's own arguments ("Generate the business case
     * for Acme Portal"). This is the step's user-facing label; `reason` above
     * is the tool's static confirmation warning and is not a per-step
     * description. Optional: absent when the planner did not author one (no
     * summary property could be injected into the tool's schema, or the model
     * omitted it), and pre-COP-122 rows carry none — they hash identically.
     */
    summary: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    args: Type.Unknown(),
    /**
     * COP-119 — declared step-output references for this step's `args`. Additive
     * and optional: absent ≡ no references, so pre-COP-119 rows need no
     * migration and hash identically.
     */
    refs: Type.Optional(Type.Array(PlanStepRefSchema, { maxItems: 50 })),
    /**
     * COP-111 §5 (XT-07; PLAN-17 auditability) — the PROVIDER track whose
     * tool this step invokes. Stamped at plan-persist time from the step
     * tool's registered descriptor (`descriptor.providerId`); `tf_plan.track_id`
     * stays the HOME track. Optional: pre-M18 rows (and steps whose tool has
     * no registered track descriptor) carry no stamp.
     */
    providerTrackId: Type.Optional(Type.String({ format: 'uuid' })),
}, { additionalProperties: false });
/**
 * Browser-visible ordered step. This intentionally has `argsPreview` and no
 * `args`; TypeBox's exact object shape rejects raw executable args in stream
 * payload tests before a card can ship.
 */
export const PlanPreviewStepSchema = Type.Object({
    index: Type.Integer({ minimum: 1 }),
    toolName: Type.String({ minLength: 1, maxLength: 200 }),
    kind: PlanStepKindSchema,
    reason: Type.String({ minLength: 1, maxLength: 1000 }),
    /**
     * COP-122 — the planner-authored step description, mirrored onto the
     * browser-visible step. It is the label the plan card and the execution
     * checklist render; `reason` stays the risk warning. Carries no value the
     * `argsPreview` redaction would have removed — the planner is instructed
     * to write a user-facing sentence, not to echo argument values.
     */
    summary: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    argsPreview: Type.Unknown(),
    references: Type.Optional(Type.Array(PlanStepReferenceSchema, { maxItems: 50 })),
    /**
     * COP-119 §5 — the declared step-output references, mirrored onto the
     * browser-visible step so the card can render them as prose ("the estimate
     * created in step 1"). Metadata only: it names an index and two paths, never
     * a value, so it carries nothing `argsPreview` redaction would have removed.
     */
    refs: Type.Optional(Type.Array(PlanStepRefSchema, { maxItems: 50 })),
}, { additionalProperties: false });
/** COP-072: plan options embedded in the stream card and persisted with the plan. */
export const PlanPendingOptionsSchema = Type.Object({
    allowWholePlanApproval: Type.Boolean(),
    wholePlanApprovalLabel: Type.String({ minLength: 1, maxLength: 200 }),
    perStepApprovalRequiredByDefault: Type.Literal(true),
}, { additionalProperties: false });
/** Drizzle-row JSON view of `tf_plan` (COP-057). */
export const PlanRowSchema = Type.Object({
    id: UuidSchema,
    sessionId: UuidSchema,
    ownerUserId: UuidSchema,
    trackId: UuidSchema,
    steps: Type.Array(PlanPersistedStepSchema, { minItems: 1, maxItems: 100 }),
    options: PlanPendingOptionsSchema,
    payloadHash: PlanPayloadHashSchema,
    status: PlanStatusSchema,
    createdAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    decidedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
}, { additionalProperties: false });
/** Drizzle-row JSON view of append-only `tf_plan_decision` (COP-071). */
export const PlanDecisionRowSchema = Type.Object({
    id: UuidSchema,
    planId: UuidSchema,
    sessionId: UuidSchema,
    ownerUserId: UuidSchema,
    trackId: UuidSchema,
    actorType: PlanDecisionActorTypeSchema,
    actorId: Type.Union([UuidSchema, Type.Null()]),
    action: PlanDecisionActionSchema,
    payloadHash: PlanPayloadHashSchema,
    payloadHashVerified: Type.Boolean(),
    wholePlanApproved: Type.Boolean(),
    reason: Type.Union([Type.String({ maxLength: 2000 }), Type.Null()]),
    metadata: JsonObjectSchema,
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/** Client-safe plan projection for decision responses. Never includes executable steps or owner-scoped raw args. */
export const PlanDecisionPlanSchema = Type.Object({
    id: UuidSchema,
    sessionId: UuidSchema,
    trackId: UuidSchema,
    payloadHash: PlanPayloadHashSchema,
    status: PlanStatusSchema,
    createdAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    decidedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    options: PlanPendingOptionsSchema,
}, { additionalProperties: false });
/** Client-safe decision projection for decision responses. Lifecycle metadata stays server-side. */
export const PlanDecisionPublicSchema = Type.Object({
    id: UuidSchema,
    planId: UuidSchema,
    sessionId: UuidSchema,
    trackId: UuidSchema,
    actorType: PlanDecisionActorTypeSchema,
    actorId: Type.Union([UuidSchema, Type.Null()]),
    action: PlanDecisionActionSchema,
    payloadHash: PlanPayloadHashSchema,
    payloadHashVerified: Type.Boolean(),
    wholePlanApproved: Type.Boolean(),
    reason: Type.Union([Type.String({ maxLength: 2000 }), Type.Null()]),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/** Decide success response: client-safe projected plan state plus lifecycle audit metadata. */
export const PlanDecideResponseSchema = Type.Object({
    plan: PlanDecisionPlanSchema,
    decision: PlanDecisionPublicSchema,
}, { additionalProperties: false });
/** Closed union for `POST /plans/:id/decide` 409 Conflict codes. */
export const PlanConflictCodeSchema = Type.Union([
    Type.Literal('hash_mismatch'),
    Type.Literal('already_decided'),
    Type.Literal('plan_expired'),
]);
/**
 * HTTP conflict envelope for plan decision replay/freshness failures.
 *
 * `expiresAt` is present for `code: 'plan_expired'` so clients can render the
 * original freshness deadline without parsing message text. No executable plan
 * args or step payloads are part of this response surface.
 */
export const PlanConflictResponseSchema = Type.Object({
    error: PlanConflictCodeSchema,
    code: PlanConflictCodeSchema,
    message: Type.String({ maxLength: 500 }),
    expiresAt: Type.Optional(IsoDateTimeSchema),
}, { additionalProperties: false });
/** Browser-visible plan payload nested under `PlanPendingPart.plan`. */
export const PlanPendingPlanSchema = Type.Object({
    id: UuidSchema,
    sessionId: UuidSchema,
    trackId: UuidSchema,
    status: Type.Literal('pending'),
    payloadHash: PlanPayloadHashSchema,
    expiresAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
    steps: Type.Array(PlanPreviewStepSchema, { minItems: 1, maxItems: 100 }),
    options: PlanPendingOptionsSchema,
}, { additionalProperties: false });
/** Exact client decision methods and stable order for the plan card. */
export const PlanPendingMethodsSchema = Type.Tuple([
    Type.Literal('approve'),
    Type.Literal('reject'),
    Type.Literal('edit'),
]);
/** Convenience route metadata for clients/proxies; the server route remains authoritative. */
export const PlanPendingActionsSchema = Type.Object({
    decideUrl: Type.String({ minLength: 1, maxLength: 2048 }),
    methods: PlanPendingMethodsSchema,
}, { additionalProperties: false });
/** Human-facing copy for the plan card. */
export const PlanPendingCopySchema = Type.Object({
    title: Type.String({ minLength: 1, maxLength: 200 }),
    summary: Type.String({ minLength: 1, maxLength: 2000 }),
    riskNotice: Type.Optional(Type.String({ minLength: 1, maxLength: 2000 })),
    expiryNotice: Type.String({ minLength: 1, maxLength: 500 }),
}, { additionalProperties: false });
/**
 * COP-072 `tf-plan-pending` payload carried inside an AI SDK
 * `data-tf-plan-pending` stream chunk.
 */
export const PlanPendingPartSchema = Type.Object({
    type: Type.Literal('tf-plan-pending'),
    plan: PlanPendingPlanSchema,
    actions: PlanPendingActionsSchema,
    copy: PlanPendingCopySchema,
}, { additionalProperties: false });
// COP-092 retire plan-mode: the `data-tf-plan-activation-notice` SSE part
// (PLAN_ACTIVATION_NOTICE_PART_TYPE / PlanActivationSourceSchema /
// PlanActivationNoticePartSchema) is removed with the activation resolver. The
// KEEP path is `PlanPendingPartSchema` (PlanCard) + the new planner-status part.
/**
 * COP-092 / PLN-06 — planner sub-agent status indicator.
 *
 * Streamed as an AI SDK `data-tf-planner-status` chunk while the read-only
 * planner sub-agent runs behind the `plan` tool. The lifecycle is:
 *  - `started`  — emitted once when the planner spawns;
 *  - `finished` — emitted once on a successful plan (before / alongside the
 *    `data-tf-plan-pending` card);
 *  - `aborted`  — emitted once when the planner is cut short (budget exceeded,
 *    planner error, empty / malformed plan) and NO PlanCard is produced.
 *
 * COP-023 redaction (PLN-06 acceptance): this payload carries NO prompt body,
 * NO read result, NO tool args, and NO model output — only the lifecycle state
 * and, for `aborted`, an enumerated machine `reasonCode`. `additionalProperties:
 * false` enforces that nothing else may piggy-back on the wire.
 */
export const PLANNER_STATUS_PART_TYPE = 'tf-planner-status';
/** Enumerated abort reasons for `data-tf-planner-status{state:'aborted'}` (PLN-06/PLN-07). */
export const PlannerStatusAbortReasonSchema = Type.Union([
    Type.Literal('budget_exceeded'),
    Type.Literal('planner_error'),
    Type.Literal('empty_plan'),
    Type.Literal('malformed_plan'),
]);
export const PlannerStatusPartSchema = Type.Union([
    Type.Object({
        type: Type.Literal(PLANNER_STATUS_PART_TYPE),
        state: Type.Union([Type.Literal('started'), Type.Literal('finished')]),
    }, { additionalProperties: false }),
    Type.Object({
        type: Type.Literal(PLANNER_STATUS_PART_TYPE),
        state: Type.Literal('aborted'),
        reasonCode: PlannerStatusAbortReasonSchema,
    }, { additionalProperties: false }),
]);
/**
 * Synthetic terminal-state marker for a plan card, emitted ONLY by session-reload
 * hydration (`src/chat/hydrate-transcript.ts`). The live runtime never emits this:
 * during a live turn the PlanCard flips locally on the user's decide click. But a
 * `data-tf-plan-pending` part is locked to `status: 'pending'` (the client
 * normalizer drops any other status), so on reload a decided plan needs a separate
 * signal. The client `collectPlanTerminalStates` pre-pass consumes this to flip the
 * reloaded card to its terminal state (no actionable controls). Mirrors the
 * approval / schedule-preview resolved-part pattern.
 */
export const PLAN_RESOLVED_PART_TYPE = 'tf-plan-resolved';
export const PlanResolvedDecisionSchema = Type.Union([
    Type.Literal('approved'),
    Type.Literal('rejected'),
    Type.Literal('edited'),
    Type.Literal('expired'),
]);
export const PlanResolvedPartSchema = Type.Object({
    type: Type.Literal(PLAN_RESOLVED_PART_TYPE),
    planId: UuidSchema,
    decision: PlanResolvedDecisionSchema,
}, { additionalProperties: false });
//# sourceMappingURL=plans.js.map